// services/articles.js — чистите функции на артикулите (ПРОМТ-7).
//
// Кодът на артикула е ТЕКСТ с водещи нули („000007"), уникален в рамките на
// фирмата. Никъде не минава през Number(): „7" и „000007" са различни кодове,
// а Number би ги слял (същият капан като normalizeId в util.js).
//
// Единиците: артикулът има БАЗОВА мерна единица (measure — тази на склада) и
// преобразувания [{ unit, factor }]: 1 unit = factor × базова (кашон = 24 бр.,
// касетка = 6 kg). toBase/fromBase са ЕДИНСТВЕНОТО място, където се
// преобразува — складът, доставките и POS-ът ще ги викат.

const { round2 } = require('./totals');

const TYPES = ['goods', 'service', 'material', 'dish'];
const CODE_RE = /^\d{6,}$/;

// Следващият свободен шестцифрен код. Броят се само чисто цифровите кодове с
// поне 6 цифри (автоматичните); ръчни кодове като „A-15" или „7" не пречат.
function nextCode(existingCodes) {
  let max = 0;
  for (const c of (Array.isArray(existingCodes) ? existingCodes : [])) {
    const s = String(c == null ? '' : c).trim();
    if (!CODE_RE.test(s)) continue;
    const n = parseInt(s, 10);
    if (n > max) max = n;
  }
  return String(max + 1).padStart(6, '0');
}

function conversionsOf(article) {
  return (article && Array.isArray(article.conversions)) ? article.conversions : [];
}

// Една и съща мярка, написана различно (ПРОМТ-13б). Касата пише „бр",
// артикулът от Конто е „брой", рецептата — „гр": без тази таблица всяка
// напитка спираше с „непозната мярка", а човекът няма как да знае защо.
// Първото във всеки ред е каноничното име. ЕДИНСТВЕНОТО място с тези списъци;
// екранът има огледално копие (src/shared/units.js), сверявано с тест.
const UNIT_SYNONYMS = [
  ['бр', 'брой', 'броя', 'бройка', 'pcs'],
  ['кг', 'kg'],
  ['г', 'гр', 'g'],
  ['л', 'l'],
  ['мл', 'ml'],
];
// Стандартните метрични преобразувания: 1 [0] = [2] × [1]. Така „г → кг" и
// „мл → л" не се въвеждат на всеки материал поотделно.
const METRIC = [
  ['г', 'кг', 0.001],
  ['мл', 'л', 0.001],
];
const UNIT_CANON = new Map();
for (const row of UNIT_SYNONYMS) for (const u of row) UNIT_CANON.set(u, row[0]);

// Ключът за сравнение на мярка: без значение главни/малки букви, точка
// накрая и интервали; синонимите се свеждат до каноничното име.
function unitKey(unit) {
  const s = String(unit == null ? '' : unit).toLowerCase().replace(/\s+/g, '').replace(/\.+$/, '');
  return UNIT_CANON.get(s) || s;
}

// Стандартният коефициент от мярка към базова (по ключове) или null.
function metricFactor(fromKey, baseKey) {
  for (const [small, big, f] of METRIC) {
    if (fromKey === small && baseKey === big) return f;
    if (fromKey === big && baseKey === small) return 1 / f;
  }
  return null;
}

// Как е позната мярката за артикула: { unit, factor } или null.
// Редът е важен: точното име → преобразуване на артикула (и под друго
// изписване) → същата мярка като базовата → стандартното метрично.
// Преобразуване, зададено на артикула, ПЕЧЕЛИ пред стандартното.
function resolve(article, unit) {
  const base = String((article && article.measure) || '').trim();
  const u = String(unit == null ? '' : unit).trim();
  if (u === base || (!u && base)) return { unit: base, factor: 1 };
  const ok = (c) => c && Number(c.factor) > 0;
  const convs = conversionsOf(article);
  const exact = convs.find((c) => String(c.unit || '').trim() === u);
  if (ok(exact)) return { unit: String(exact.unit).trim(), factor: Number(exact.factor) };
  const ku = unitKey(u);
  const kb = unitKey(base);
  if (!ku) return null;
  const loose = convs.find((c) => unitKey(c.unit) === ku);
  if (ok(loose)) return { unit: String(loose.unit).trim(), factor: Number(loose.factor) };
  if (kb && ku === kb) return { unit: base, factor: 1 };
  const f = metricFactor(ku, kb);
  return f == null ? null : { unit: ku, factor: f };
}

// Мярката от Excel/POS → име, което toBase приема, или null. Непозната
// единица НЕ се пада към базовата: 200 „пакета" при база кг не са 200 кг.
function resolveUnit(article, unit) {
  const r = resolve(article, unit);
  return r ? r.unit : null;
}

function factorOf(article, unit) {
  const r = resolve(article, unit);
  if (!r) {
    const u = String(unit == null ? '' : unit).trim();
    throw new Error('Непозната мерна единица „' + u + '" за артикула' + (article && article.name ? ' „' + article.name + '"' : '') + '.');
  }
  return r.factor;
}

// Количество в единица `unit` → количество в базовата единица.
// round2-безопасно: 0.5 касетки × 6 = 3, не 2.9999999.
function toBase(article, qty, unit) {
  const q = Number(qty) || 0;
  return Math.round(q * factorOf(article, unit) * 1e6) / 1e6;
}

// Обратното: количество в базовата единица → в единица `unit`.
function fromBase(article, qty, unit) {
  const q = Number(qty) || 0;
  return Math.round((q / factorOf(article, unit)) * 1e6) / 1e6;
}

// Проверка на списъка с преобразувания. Връща текст на грешката или null.
function validateConversions(list, measure) {
  if (list == null) return null;
  if (!Array.isArray(list)) return 'Преобразуванията трябва да са списък.';
  const base = String(measure || '').trim();
  const seen = new Set();
  for (const c of list) {
    const unit = String((c && c.unit) || '').trim();
    const factor = Number(c && c.factor);
    if (!unit) return 'Всяко преобразуване трябва да има мерна единица.';
    if (unit === base) return 'Единицата „' + unit + '" е базовата — преобразуване към самата себе си няма смисъл.';
    if (!(factor > 0)) return 'Коефициентът за „' + unit + '" трябва да е положително число (колко базови единици е 1 ' + unit + ').';
    if (seen.has(unit)) return 'Единицата „' + unit + '" се повтаря.';
    seen.add(unit);
  }
  return null;
}

// Каноничният вид на списъка за запис.
function cleanConversions(list) {
  if (!Array.isArray(list)) return [];
  return list.map((c) => ({ unit: String((c && c.unit) || '').trim().slice(0, 30), factor: Number(c && c.factor) }));
}

const ACCOUNT_KEYS = ['stock_account', 'revenue_account', 'cogs_account'];

function nonEmpty(v) {
  return v !== undefined && v !== null && String(v).trim() !== '';
}

// Трите сметки след наследяването: артикул → група → подразбиране по тип
// (defaults = kontoConfig.DEFAULTS или ефективната конфигурация на фирмата).
function effectiveAccounts(article, group, defaults) {
  const a = article || {};
  const g = group || {};
  const byType = ((defaults && defaults.accounts_by_type) || {})[a.type || 'service'] || {};
  const out = {};
  for (const k of ACCOUNT_KEYS) {
    out[k] = nonEmpty(a[k]) ? String(a[k]) : nonEmpty(g[k]) ? String(g[k]) : (byType[k] == null ? null : String(byType[k]));
  }
  return out;
}

// Кодовете за Конто на артикула: { code, measure, article_type }.
// Празно поле в accounting.konto6 = подразбиране: кодът на артикула, мярка
// '001' (или кодът на базовата единица от measure_codes), тип от групата или 50.
function kontoOf(article, group, defaults) {
  const a = article || {};
  const k6 = (a.accounting && a.accounting.konto6) || {};
  const measureCodes = (defaults && defaults.measure_codes) || {};
  const defMeasure = (defaults && defaults.measure) || '001';
  const defType = (defaults && defaults.article_type != null) ? defaults.article_type : 50;
  const g = group || {};
  return {
    code: nonEmpty(k6.code) ? String(k6.code) : String(a.code || ''),
    measure: nonEmpty(k6.measure) ? String(k6.measure) : (measureCodes[a.measure] || defMeasure),
    article_type: nonEmpty(k6.article_type) ? Number(k6.article_type) : (nonEmpty(g.konto_article_type) ? Number(g.konto_article_type) : defType),
  };
}

// Групите по подразбиране при включване на склада за фирма без групи.
function defaultGroups(defaults) {
  const byType = (defaults && defaults.accounts_by_type) || {};
  const defType = (defaults && defaults.article_type != null) ? defaults.article_type : 50;
  const mk = (name, type) => Object.assign({ name }, {
    stock_account: (byType[type] || {}).stock_account == null ? null : String(byType[type].stock_account),
    revenue_account: (byType[type] || {}).revenue_account == null ? null : String(byType[type].revenue_account),
    cogs_account: (byType[type] || {}).cogs_account == null ? null : String(byType[type].cogs_account),
    konto_article_type: defType,
    active: true,
  });
  return [mk('Стоки', 'goods'), mk('Услуги', 'service'), mk('Материали', 'material')];
}

// Нов артикул със стойностите по подразбиране — за местата, които създават
// артикули без формата (ястията от касата и от вноса на рецептури, ПРОМТ-13б).
// Полетата са същите, които дава shape() в routes/articles.js за празна форма.
// Мярка „бр/брой/pcs" се записва като „бр." — както я пише формата.
function blank(fields) {
  const f = fields || {};
  const unit = String(f.measure == null ? '' : f.measure).trim().slice(0, 30);
  return {
    company_id: Number(f.company_id) || null,
    code: String(f.code || ''),
    name: String(f.name == null ? '' : f.name).trim().slice(0, 300),
    type: TYPES.includes(f.type) ? f.type : 'service',
    unit_price: 0,
    measure: !unit || unitKey(unit) === 'бр' ? 'бр.' : unit,
    vat_rate: 20,
    barcode: '',
    group_id: null,
    min_qty: 0,
    conversions: [],
    accounting: { konto6: { code: '', measure: '', article_type: null } },
    stock_account: null,
    revenue_account: null,
    cogs_account: null,
    last_purchase_price: null,
    active: true,
  };
}

// Артикул без код е заварен (отпреди ПРОМТ-7) — складът го третира като
// неактивен, докато не мине миграцията (scripts/migrate-article-codes.js).
function hasCode(article) {
  return nonEmpty(article && article.code);
}

module.exports = {
  TYPES, ACCOUNT_KEYS, UNIT_SYNONYMS, METRIC,
  unitKey, resolveUnit, blank,
  nextCode, toBase, fromBase, validateConversions, cleanConversions,
  effectiveAccounts, kontoOf, defaultGroups, hasCode, round2,
};
